import { Button, Checkbox, Select, Table, Typography, theme } from "antd";
import Icon from "@ant-design/icons";
import { Icons } from "../../assets/icons";
import TargetListFrame, { frameEmptyText } from "./TargetListFrame";
import { useEffect, useRef, useState } from "react";
import type { RefSelectProps } from "antd/es/select";
import { InputObject } from "../../controls/default";
import { subscription, Subscription } from "../../utils/http";

export interface DepartmentsMultiSelectProps {
  /** The company whose departments can be picked. Clearing it clears the picker and empties the chip list. */
  readonly companyUid?: string;
  /** Currently-selected departments (controlled). */
  readonly value: InputObject[];
  readonly onChange: (next: InputObject[]) => void;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly label?: string;
  readonly placeholder?: string;
  /** Show an "Add all" toggle pinned at the top of the dropdown: adds every department of the company;
   *  unticking clears all departments. */
  readonly allowAddAll?: boolean;
  /** When the company switches, the picker reloads; the chip list is kept intact — the parent decides whether
   *  stale chips (belonging to the previous company) should also be cleared. */
  readonly loadDepartments: (
    companyUid: string | undefined,
    search: string,
  ) => Promise<InputObject[]>;
}

/**
 * Picker for an event's target departments:
 *   • a searchable checklist Select scoped to the event's company — added departments show checked,
 *     clicking toggles them, the box shows a count (optional "Add All" pinned on top)
 *   • a compact table of the added departments (#, name, remove)
 *
 * Sits outside DynamicForm since the shared combobox is single-value only.
 */
function DepartmentsMultiSelect(props: DepartmentsMultiSelectProps) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<InputObject[]>([]);
  const [subscribe, setSubscribe] = useState<Subscription | null>(null);
  const { token } = theme.useToken();
  const selectRef = useRef<RefSelectProps>(null);
  // On after "Add All" selected every department; any removal turns it off.
  const [addAll, setAddAll] = useState(false);

  // Reload options whenever the scope company changes (also seeds the dropdown with "" for initial open).
  useEffect(() => {
    setAddAll(false);
    loadData("");
    return () => subscribe?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.companyUid]);

  function loadData(search: string): void {
    subscribe?.unsubscribe();
    setLoading(true);
    const $$ = subscription(
      () => props.loadDepartments(props.companyUid, search),
      200,
    );
    setSubscribe($$);
    $$.then((data) => {
      setOptions(data || []);
      setLoading(false);
    }).catch(() => {
      setOptions([]);
      setLoading(false);
    });
  }

  function onAdd(uid: string): void {
    const picked = options.find((o) => o.value === uid);
    if (!picked || props.value.some((d) => d.value === uid)) return;
    props.onChange([...props.value, picked]);
  }

  function onRemove(uid: string): void {
    props.onChange(props.value.filter((d) => d.value !== uid));
    // Removing one by hand means "all" no longer holds — the rest stay, the toggle turns off.
    setAddAll(false);
  }

  function onAddAllChange(checked: boolean): void {
    // "Add All" selects every department, so unticking it clears them all — regardless of which ones
    // were picked by hand or removed and re-added along the way.
    if (!checked) {
      props.onChange([]);
      setAddAll(false);
      return;
    }
    setLoading(true);
    props
      .loadDepartments(props.companyUid, "")
      .then((all) => {
        const added = (all || []).filter((o) => !props.value.some((d) => d.value === o.value));
        setAddAll(true);
        if (added.length) props.onChange([...props.value, ...added]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  const disabled = props.disabled || props.readOnly || !props.companyUid;
  return (
    <div className="flex flex-col gap-3">
      {props.label && (
        <Typography.Text className="text-sm" type="secondary">
          {props.label}
        </Typography.Text>
      )}
      {!props.readOnly && (
        <Select
          ref={selectRef}
          className="w-full"
          disabled={disabled}
          loading={loading}
          // A checklist: added departments show checked, clicking one again removes it, and the dropdown
          // stays open between clicks. Chips are hidden — the table below lists them; the box counts.
          mode="multiple"
          value={props.value.map((d) => d.value)}
          maxTagCount={0}
          maxTagPlaceholder={(omitted) => `${omitted.length} added`}
          autoClearSearchValue={false}
          placeholder={
            props.placeholder ??
            (!props.companyUid
              ? "Pick a company first"
              : "Search and add a department")
          }
          showSearch
          filterOption={false}
          onSearch={loadData}
          onSelect={onAdd}
          onDeselect={onRemove}
          notFoundContent={loading ? undefined : "No departments found"}
          dropdownRender={(menu) =>
            props.allowAddAll ? (
              <>
                <div
                  className="mb-1 border-0 border-b border-solid px-3 pb-2 pt-1"
                  style={{ borderColor: token.colorBorderSecondary }}
                  // Focus stays in the search box while clicking; then "Add All" closes the dropdown.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectRef.current?.blur()}
                >
                  <Checkbox
                    checked={addAll}
                    disabled={props.disabled || loading}
                    onChange={(e) => onAddAllChange(e.target.checked)}
                  >
                    Add All
                  </Checkbox>
                </div>
                {menu}
              </>
            ) : (
              menu
            )
          }
          options={options.map((o) => ({ value: o.value, label: o.display }))}
        />
      )}
      <TargetListFrame>
        <Table<InputObject>
          sticky
          size="small"
          rowKey="value"
          pagination={false}
          dataSource={props.value}
          locale={{ emptyText: frameEmptyText("No departments added") }}
          columns={[
            {
              title: "#",
              key: "index",
              width: 40,
              render: (_, __, index) => (
                <Typography.Text type="secondary">{index + 1}</Typography.Text>
              ),
            },
            {
              title: `Department${props.value.length ? ` (${props.value.length})` : ""}`,
              dataIndex: "display",
              ellipsis: true,
            },
            ...(props.readOnly
              ? []
              : [
                  {
                    key: "remove",
                    width: 44,
                    align: "center" as const,
                    render: (_: any, item: InputObject) => (
                      <Button
                        type="text"
                        size="small"
                        disabled={props.disabled}
                        aria-label={`Remove ${item.display}`}
                        icon={
                          <Icon
                            className="text-lg"
                            component={Icons.CloseIcon}
                          />
                        }
                        onClick={() => onRemove(item.value)}
                      />
                    ),
                  },
                ]),
          ]}
        />
      </TargetListFrame>
    </div>
  );
}

export default DepartmentsMultiSelect;
